/**
 * choreographyConfig — the declarative cue types for the Set-Piece
 * Choreography system. A `ChoreographyCue` is a data-driven trigger→timeline
 * that choreographs a timed atmosphere/FX sequence (a mood swell, a fog surge,
 * a leviathan reveal, a boss-dais darkening) — the difference between an
 * ambient scene and an EPIC one. Authored per-level in `scene.cues`; zero
 * bespoke code per moment.
 *
 * DESIGN (lift-ready checklist §2): every field optional + defaulted where
 * sensible; resolves to a flat spec object. JSON/serde-friendly so it maps 1:1
 * to a Rust `struct` with `#[serde(default)]`. Pure data — no renderer wiring.
 *
 * Composes with the existing mood bus (EVENTS.SCENE_MOOD / COMBAT_MOOD), the
 * effect kit (fx-patch any layer kind via AtmosphereLayer.applyLayers), and
 * event sources (BossPhaseManager, quests, time-of-day). The driver
 * (ChoreographyDriver.ts) is the thin runtime that watches triggers + runs
 * timelines; the model (ChoreographyModel.ts) is the pure cue-evaluation
 * engine (unit-testable with no renderer).
 */
import type { TimeOfDayPhase } from './timeOfDay.config';

// ─── trigger types ───────────────────────────────────────────────────────────

/** Fires when the player enters a world-space rectangle. */
export interface EnterRegionTrigger {
  on: 'enterRegion';
  x: number;
  y: number;
  w: number;
  h: number;
  /** Fire only once per level visit (default true — most reveals are one-shot). */
  once?: boolean;
}

/** Fires when an EventBus event is emitted (e.g. boss phase change, quest beat). */
export interface EventTrigger {
  on: 'event';
  /** The EventBus event name to listen for. */
  event: string;
  /** Optional payload matcher — fires only when the payload matches. */
  match?: Record<string, unknown>;
  /** Fire only once per level visit (default false — boss phases can repeat). */
  once?: boolean;
}

/** Fires on scene load (the "the scene opens on" beat). */
export interface MapEnterTrigger {
  on: 'mapEnter';
  /** Delay after scene load before the timeline starts (ms, default 0). */
  delayMs?: number;
}

/** Fires when the time-of-day clock enters a named phase. */
export interface TimeOfDayTrigger {
  on: 'timeOfDay';
  phase: TimeOfDayPhase;
  /** Fire only once per level visit (default false — dawn happens once but
   *  the phase may recur if the cycle is short). */
  once?: boolean;
}

export type CueTrigger = EnterRegionTrigger | EventTrigger | MapEnterTrigger | TimeOfDayTrigger;

// ─── timeline beat ───────────────────────────────────────────────────────────

/** A live-tweak patch targeting a specific layer instance. */
export interface ChoreographyFxPatch {
  /** Address: the layer's authored `id` (preferred — deterministic). */
  layerId?: string;
  /** Address: the kind name + ordinal index (fallback when no id authored). */
  layerKind?: string;
  /** For multi-instance kinds (e.g. leviathans far=0, near=1). */
  planeIndex?: number;
  /** The patch: a partial EffectLayer override applied via the live-tuner path
   *  (AtmosphereLayer.applyLayers with the patched layer list). */
  patch: Record<string, unknown>;
}

/** One beat in a cue's timeline. */
export interface CueTimelineBeat {
  /** Offset from trigger (ms). */
  atMs: number;
  /** Ease the scene-mood / combat-mood to these values over `overMs`. */
  set?: { sceneMood?: number; combatMood?: number };
  /** Fire an EventBus event at this beat. */
  emit?: { event: string; payload?: unknown };
  /** Live-tweak a layer knob (the live-tuner path). */
  fx?: ChoreographyFxPatch;
  /** Ease/transition duration for `set` / `fx` (ms, default 0 = instant). */
  overMs?: number;
}

// ─── the cue ─────────────────────────────────────────────────────────────────

/** How a cue degrades under reduced motion. */
export type ReduceMotionMode = 'full' | 'instant' | 'skip';

/**
 * A declarative trigger→timeline that choreographs a timed atmosphere/FX
 * sequence. Authored per-level in `scene.cues`; the ChoreographyDriver watches
 * the trigger and runs the timeline, composing with the mood bus + effect kit.
 */
export interface ChoreographyCue {
  /** Stable id — for de-dup, debugging, and editor keys. */
  id: string;
  /** What fires the timeline. */
  trigger: CueTrigger;
  /** The timed beats (sorted by atMs at resolve time). */
  timeline: CueTimelineBeat[];
  /** How it degrades under reduced motion (default 'instant' — jump to end
   *  state, no eases). 'skip' = don't fire at all (purely decorative cues).
   *  'full' = run the full timeline including eases. */
  reduceMotion?: ReduceMotionMode;
}

// ─── defaults + helpers ──────────────────────────────────────────────────────

export const DEFAULT_REDUCE_MOTION: ReduceMotionMode = 'instant';

/** Sort a cue's timeline beats by atMs (stable — preserves order within ties). */
export function sortedTimeline(beats: CueTimelineBeat[]): CueTimelineBeat[] {
  return [...beats].sort((a, b) => a.atMs - b.atMs);
}

/** Is a point inside an enterRegion trigger's rectangle? */
export function pointInRegion(
  px: number,
  py: number,
  trigger: EnterRegionTrigger,
): boolean {
  return px >= trigger.x && px <= trigger.x + trigger.w
    && py >= trigger.y && py <= trigger.y + trigger.h;
}

/** Does an event trigger's payload match its optional `match` filter? */
export function payloadMatches(
  payload: unknown,
  match: Record<string, unknown> | undefined,
): boolean {
  if (!match) return true;
  if (typeof payload !== 'object' || payload === null) return false;
  const p = payload as Record<string, unknown>;
  for (const [key, val] of Object.entries(match)) {
    if (p[key] !== val) return false;
  }
  return true;
}
